import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { DashboardStatisticsResponse, RecentProcessesResponse, UsersByGroupResponse, ProcessesByStatusResponse, ProcessesProgressResponse, ProcessesTimelineResponse, GroupsCompletionRateResponse, ActivityHeatmapResponse } from '../models/dashboard.model';

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  // ==========================================================================
  // PROPRIEDADES
  // ==========================================================================
  
  private readonly apiUrl = environment.apiUrl;

  // ==========================================================================
  // CONSTRUCTOR
  // ==========================================================================
  
  constructor(private http: HttpClient) {}

  // ==========================================================================
  // MÉTODOS DE DASHBOARD
  // ==========================================================================
  
  /**
   * Busca estatísticas do dashboard
   * @returns Observable com as estatísticas do dashboard
   */
  public getStatistics(): Observable<DashboardStatisticsResponse> {
    return this.http.get<DashboardStatisticsResponse>(`${this.apiUrl}/dashboard/statistics`);
  }

  /**
   * Busca os processos recentes do dashboard
   * @returns Observable com os processos recentes
   */
  public getRecentProcesses(): Observable<RecentProcessesResponse> {
    return this.http.get<RecentProcessesResponse>(`${this.apiUrl}/dashboard/recent-processes`);
  }

  /**
   * Busca usuários por grupo do dashboard
   * @returns Observable com os grupos e contagem de usuários
   */
  public getUsersByGroup(): Observable<UsersByGroupResponse> {
    return this.http.get<UsersByGroupResponse>(`${this.apiUrl}/dashboard/users-by-group`);
  }

  /**
   * Busca processos agrupados por status do dashboard
   * @returns Observable com os processos agrupados por status
   */
  public getProcessesByStatus(): Observable<ProcessesByStatusResponse> {
    return this.http.get<ProcessesByStatusResponse>(`${this.apiUrl}/dashboard/processes-by-status`);
  }

  /**
   * Busca processos com maior progresso de conclusão do dashboard
   * @param limit Quantidade de processos (padrão: 10)
   * @returns Observable com os processos ordenados por progresso
   */
  public getProcessesProgress(limit: number = 10): Observable<ProcessesProgressResponse> {
    return this.http.get<ProcessesProgressResponse>(`${this.apiUrl}/dashboard/processes-progress?limit=${limit}`);
  }

  /**
   * Busca evolução temporal de processos criados e concluídos do dashboard
   * @param period Últimos N dias (padrão: 30)
   * @param groupBy Agrupamento: day, week, month (padrão: day)
   * @returns Observable com os dados de timeline dos processos
   */
  public getProcessesTimeline(period: number = 30, groupBy: string = 'day'): Observable<ProcessesTimelineResponse> {
    return this.http.get<ProcessesTimelineResponse>(`${this.apiUrl}/dashboard/processes-timeline?period=${period}&group_by=${groupBy}`);
  }

  /**
   * Busca taxa de conclusão de questionários por grupo eclesiástico do dashboard
   * @returns Observable com os dados de taxa de conclusão dos grupos
   */
  public getGroupsCompletionRate(): Observable<GroupsCompletionRateResponse> {
    return this.http.get<GroupsCompletionRateResponse>(`${this.apiUrl}/dashboard/groups-completion-rate`);
  }

  /**
   * Busca atividades do sistema por dia da semana e hora para heatmap
   * @param period Últimos N dias (padrão: 30)
   * @returns Observable com os dados de atividade para heatmap
   */
  public getActivityHeatmap(period: number = 30): Observable<ActivityHeatmapResponse> {
    return this.http.get<ActivityHeatmapResponse>(`${this.apiUrl}/dashboard/activity-heatmap?period=${period}`);
  }
}
